import i18n, { FormatterModule } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import commonDE from "./locales/de/common.json";
import modalsDE from "./locales/de/modals.json";
import pagesDE from "./locales/de/pages.json";
import sidepanelsDE from "./locales/de/sidepanels.json";
import commonEN from "./locales/en/common.json";
import modalsEN from "./locales/en/modals.json";
import pagesEN from "./locales/en/pages.json";
import sidepanelsEN from "./locales/en/sidepanels.json";
import commonFR from "./locales/fr/common.json";
import modalsFR from "./locales/fr/modals.json";
import pagesFR from "./locales/fr/pages.json";
import sidepanelsFR from "./locales/fr/sidepanels.json";
import commonJA from "./locales/ja/common.json";
import modalsJA from "./locales/ja/modals.json";
import pagesJA from "./locales/ja/pages.json";
import sidepanelsJA from "./locales/ja/sidepanels.json";
import commonPL from "./locales/pl/common.json";
import modalsPL from "./locales/pl/modals.json";
import pagesPL from "./locales/pl/pages.json";
import sidepanelsPL from "./locales/pl/sidepanels.json";
import commonRU from "./locales/ru/common.json";
import modalsRU from "./locales/ru/modals.json";
import pagesRU from "./locales/ru/pages.json";
import sidepanelsRU from "./locales/ru/sidepanels.json";
import { buildZodLocale } from "./config.validation";
import { formatNumber } from "./formatNumber";

import "./config.dates";
import "./config.fonts";

export const defaultNS = "common";
export const fallbackLng = "en";

export const resources = {
    de: {
        common: commonDE,
        modals: modalsDE,
        pages: pagesDE,
        sidepanels: sidepanelsDE,
    },
    en: {
        common: commonEN,
        modals: modalsEN,
        pages: pagesEN,
        sidepanels: sidepanelsEN,
    },
    fr: {
        common: commonFR,
        modals: modalsFR,
        pages: pagesFR,
        sidepanels: sidepanelsFR,
    },
    pl: {
        common: commonPL,
        modals: modalsPL,
        pages: pagesPL,
        sidepanels: sidepanelsPL,
    },
    ja: {
        common: commonJA,
        modals: modalsJA,
        pages: pagesJA,
        sidepanels: sidepanelsJA,
    },
    ru: {
        common: commonRU,
        modals: modalsRU,
        pages: pagesRU,
        sidepanels: sidepanelsRU,
    },
} as const;

// Placeholders that always hold an amount get the user's digit grouping ("6.774" in German, "6,774" in English).
// Others can carry IDs or version codes (`entityId`, `version`, …) that must stay as they are, so they are left alone.
// Add a placeholder name here when a new text shows an amount.
const amountPlaceholders = new Set(["count", "total", "limit", "max", "slots", "freeLicenseSlots", "from", "to"]);

const amountFormatter: FormatterModule = {
    type: "formatter",
    init: () => undefined,
    add: () => undefined,
    addCached: () => undefined,
    format: (value, format, lng, options) =>
        !format && typeof value === "number" && amountPlaceholders.has(String(options?.interpolationkey ?? ""))
            ? formatNumber(value, lng ?? fallbackLng)
            : value,
};

export const supportedLanguages = ["de", "en", "fr", "pl", "ja", "ru"] as const satisfies ReadonlyArray<keyof typeof resources>;
export type LanguageCode = (typeof supportedLanguages)[number];

/**
 * Resets the active language to fallback when it is not in the available list.
 */
export const fallbackLanguageIfUnavailable = async (availableLanguages: readonly string[]): Promise<void> => {
    const current = i18n.language?.split("-")[0] || fallbackLng;
    if (!availableLanguages.includes(current)) {
        await i18n.changeLanguage(fallbackLng);
    }
};

void i18n
    .use(LanguageDetector)
    .use(amountFormatter)
    .use(initReactI18next)
    .init(
        {
            defaultNS,
            detection: {
                caches: ["localStorage"],
                order: ["localStorage", "navigator"],
            },
            fallbackLng,
            interpolation: { escapeValue: false, alwaysFormat: true },
            keySeparator: ".",
            ns: ["common", "modals", "pages", "sidepanels"],
            pluralSeparator: "_",
            react: { useSuspense: false },
            resources,
        },
        buildZodLocale,
    );

export default i18n;
